import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { Plus, Minus, ArrowRight, ArrowLeft, Play, Pause } from "lucide-react";
import { SERVICES } from "../lib/services";
import WavyEnd from "./WavyEnd";

/**
 * Interactive services showcase — an accordion of every service the studio
 * offers, with numbered process steps that reveal on expand.
 *
 * Design goals:
 *  - Left column = index (00 / 01 / …) + service label pill; clicking swaps
 *    which service is expanded (accordion behaviour).
 *  - Right column = current service title + short blurb + a numbered step
 *    ladder that reveals with a staggered spring animation.
 *  - Each step is a hover-affordant "capsule" — the description slides in
 *    from the number pill on hover / focus.
 *  - Fully keyboard accessible (buttons, aria-expanded, focus-visible).
 */
export default function ServicesShowcase({ eyebrow = "Services — 06", heading = "How we work,\nstep by step." }) {
  const [openIdx, setOpenIdx] = useState(0);
  // Which step within the active service is currently shown (one at a time).
  // Keyed by service code so switching services remembers each service's step.
  const [stepByService, setStepByService] = useState({});
  const [autoplay, setAutoplay] = useState(false);
  const active = SERVICES[openIdx];
  const ActiveIcon = active.icon;
  const stepIdx = stepByService[active.code] ?? 0;
  const step = active.steps[stepIdx];
  const totalSteps = active.steps.length;
  const isLast = stepIdx === totalSteps - 1;
  const isFirst = stepIdx === 0;
  const setStep = (i) => setStepByService((prev) => ({ ...prev, [active.code]: i }));
  const selectService = (i) => {
    setOpenIdx(i);
    // Reset step-index for the newly opened service so the first step shows.
    const code = SERVICES[i].code;
    setStepByService((prev) => ({ ...prev, [code]: 0 }));
  };

  // Autoplay — advance one step every 5s while playing. Loops within the
  // current service (so recruiters can leave it on any service and watch it
  // cycle). Respects prefers-reduced-motion.
  useEffect(() => {
    if (!autoplay) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setAutoplay(false);
      return;
    }
    const t = setInterval(() => {
      setStepByService((prev) => {
        const curr = prev[active.code] ?? 0;
        const next = (curr + 1) % totalSteps;
        return { ...prev, [active.code]: next };
      });
    }, 5000);
    return () => clearInterval(t);
  }, [autoplay, active.code, totalSteps]);

  // Panel body — extracted so it can render either (a) inline below the tapped
  // service on mobile, or (b) in the right column on desktop, without cloning
  // the JSX. `loc` distinguishes them so AnimatePresence keys stay unique.
  const renderActivePanel = (loc) => (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${loc}-${active.code}`}
        id={loc === "desktop" ? `service-panel-${active.code}` : `service-panel-${active.code}-inline`}
        role="region"
        aria-labelledby={loc === "desktop" ? `service-title-${active.code}` : `service-title-${active.code}-inline`}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -14 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="pt-6 md:pt-10 pb-4"
        itemScope
        itemType="https://schema.org/Service"
        data-testid={`service-panel-${active.code}${loc === "mobile" ? "-inline" : ""}`}
      >
        <div className="flex items-baseline gap-4 mb-6">
          <ActiveIcon size={28} strokeWidth={1} className="text-ash" aria-hidden />
          <span className="mono-label text-marine">{active.label}</span>
        </div>
        <h3
          id={loc === "desktop" ? `service-title-${active.code}` : `service-title-${active.code}-inline`}
          className="font-display italic text-2xl md:text-4xl leading-tight tracking-tight mb-3"
          itemProp="name"
        >
          {active.title}
        </h3>
        <p className="text-ash text-sm md:text-base leading-relaxed max-w-[62ch]" itemProp="description">
          {active.detail}
        </p>
        <WavyEnd className="text-marine" width={120} />

        {/* Step counter + autoplay toggle + progress rail */}
        <div className="mt-10 mb-4 flex items-center gap-4">
          <span className="mono-label text-marine tabular-nums" data-testid={`step-counter-${active.code}${loc === "mobile" ? "-inline" : ""}`}>
            Step {String(stepIdx + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
          </span>

          {/* Autoplay pill */}
          <button
            type="button"
            onClick={() => setAutoplay((a) => !a)}
            aria-label={autoplay ? "Pause autoplay" : "Play autoplay"}
            aria-pressed={autoplay}
            title={autoplay ? "Pause" : "Watch the steps"}
            className={`group flex items-center gap-1.5 mono-label text-[10px] px-2.5 py-1 border transition-colors duration-300 ${autoplay ? "bg-marine border-marine text-paper" : "border-ink/25 text-ash hover:border-marine hover:text-marine"}`}
            style={{ borderRadius: "999px" }}
            data-testid={`step-autoplay-${active.code}${loc === "mobile" ? "-inline" : ""}`}
            data-cursor="hover"
          >
            {autoplay ? (
              <Pause size={11} strokeWidth={1.6} fill="currentColor" />
            ) : (
              <Play size={11} strokeWidth={1.6} fill="currentColor" />
            )}
            <span>{autoplay ? "Playing" : "Watch"}</span>
          </button>

          <div className="flex-1 h-px bg-ink/10 relative overflow-hidden" aria-hidden>
            <motion.div
              className="absolute left-0 top-0 h-full bg-marine"
              initial={false}
              animate={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            />
            {autoplay && (
              <motion.div
                key={`auto-${loc}-${active.code}-${stepIdx}`}
                className="absolute left-0 top-0 h-full bg-marine/50"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 5, ease: "linear" }}
                style={{ mixBlendMode: "multiply" }}
              />
            )}
          </div>
        </div>

        {/* Single-step carousel */}
        <div className="border-t border-ink/10 border-b border-ink/10 min-h-[220px] py-8 md:py-10 relative overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`${loc}-${active.code}-${stepIdx}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-[auto_1fr] gap-5 md:gap-8 items-start"
              data-testid={`step-${active.code}-${stepIdx}${loc === "mobile" ? "-inline" : ""}`}
            >
              <div
                className="relative w-20 md:w-28 aspect-square border border-marine/40 bg-bone/40 flex flex-col items-center justify-center overflow-hidden shrink-0"
                aria-hidden
              >
                <div
                  className="absolute inset-0 opacity-40 pointer-events-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, rgba(22,64,63,0.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(22,64,63,0.10) 1px, transparent 1px)",
                    backgroundSize: "12px 12px",
                  }}
                />
                <motion.span
                  className="absolute -top-2 -right-2 w-10 h-px bg-marine/60 origin-right"
                  style={{ transform: "rotate(-45deg)" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.85, rotate: -8 }}
                  animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                  transition={{ duration: 0.55, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="text-marine"
                >
                  {step.cue ? (
                    <step.cue size={30} strokeWidth={1.2} className="md:w-9 md:h-9" />
                  ) : (
                    <ActiveIcon size={30} strokeWidth={1.2} className="md:w-9 md:h-9" />
                  )}
                </motion.div>
                <span className="mono-label text-marine/80 text-[10px] mt-1.5 tabular-nums relative">
                  {String(step.n).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
                </span>
              </div>

              <div>
                <div className="font-display italic text-lg md:text-2xl leading-tight tracking-tight">
                  {step.title}
                </div>
                <p className="text-ash text-sm md:text-[15px] leading-relaxed mt-3 max-w-[58ch]">
                  {step.body}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Step navigation — playful next button */}
        <div className="mt-6 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setStep(Math.max(0, stepIdx - 1))}
            disabled={isFirst}
            className="mono-label flex items-center gap-2 text-ash hover:text-marine disabled:opacity-30 disabled:hover:text-ash transition-colors duration-300"
            aria-label="Previous step"
            data-testid={`step-prev-${active.code}${loc === "mobile" ? "-inline" : ""}`}
            data-cursor="hover"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            <span>Prev</span>
          </button>

          <div className="flex items-center gap-2" role="tablist" aria-label="Jump to step">
            {active.steps.map((_, si) => (
              <button
                key={`${loc}-${active.code}-dot-${si}`}
                type="button"
                onClick={() => setStep(si)}
                aria-label={`Go to step ${si + 1}`}
                aria-current={si === stepIdx}
                className={`transition-all duration-500 ${si === stepIdx ? "w-6 h-2 bg-marine" : "w-2 h-2 bg-ink/25 hover:bg-ink/45"}`}
                style={{ borderRadius: "999px" }}
                data-testid={`step-dot-${active.code}-${si}${loc === "mobile" ? "-inline" : ""}`}
              />
            ))}
          </div>

          {isLast ? (
            <Link
              to="/contact"
              className="group mono-label flex items-center gap-2 px-5 py-2.5 bg-marine text-paper hover:bg-ink transition-colors duration-300"
              style={{ borderRadius: "999px" }}
              data-testid={`step-final-${active.code}${loc === "mobile" ? "-inline" : ""}`}
              data-cursor="hover"
            >
              <span>Enquire</span>
              <motion.span
                aria-hidden
                animate={{ x: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
              >
                <ArrowRight size={14} strokeWidth={1.5} />
              </motion.span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setStep(Math.min(totalSteps - 1, stepIdx + 1))}
              className="group mono-label flex items-center gap-2 px-5 py-2.5 bg-ink text-paper hover:bg-marine transition-colors duration-300"
              style={{ borderRadius: "999px" }}
              data-testid={`step-next-${active.code}${loc === "mobile" ? "-inline" : ""}`}
              data-cursor="hover"
            >
              <span>Next step</span>
              <motion.span
                aria-hidden
                className="inline-block"
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
              >
                <ArrowRight size={14} strokeWidth={1.5} />
              </motion.span>
            </button>
          )}
        </div>

        <div className="mt-8">
          <Link
            to={`/studio#${active.code}`}
            className="mono-label link-underline text-marine"
            data-testid={`service-more-${active.code}${loc === "mobile" ? "-inline" : ""}`}
          >
            Full page on Studio →
          </Link>
        </div>

        <meta itemProp="serviceType" content={active.title} />
        <meta itemProp="areaServed" content="Sydney, NSW, Australia" />
      </motion.div>
    </AnimatePresence>
  );

  return (
    <section
      id="services"
      aria-labelledby="services-showcase-heading"
      className="container-w py-32 md:py-40 border-t border-ink/10 relative"
      data-testid="services-showcase"
    >
      {/* Ambient number */}
      <div aria-hidden className="pointer-events-none select-none absolute -left-6 top-24 rotate-[-90deg] origin-left mono-label text-ash/40">
        — {eyebrow}
      </div>

      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
        <div className="md:col-span-5">
          <div className="mono-label mb-4">What we offer</div>
          <h2
            id="services-showcase-heading"
            className="font-display italic text-4xl md:text-6xl leading-[0.95] tracking-tight whitespace-pre-line"
          >
            {heading}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-ash text-base md:text-lg leading-relaxed max-w-[52ch]">
            Every project follows a considered path — from brief to certificate. Tap any service below to see the studio's process, step by step.
          </p>
          <WavyEnd className="text-marine" width={160} />
        </div>
      </div>

      {/* Body — accordion */}
      <LayoutGroup id="services-showcase">
        <div
          className="grid grid-cols-1 md:grid-cols-12 md:gap-x-8 border-t border-ink/10"
          itemScope
          itemType="https://schema.org/OfferCatalog"
        >
          {/* Left rail — service list */}
          <ol className="md:col-span-5 md:border-r border-ink/10" data-testid="services-rail">
            {SERVICES.map((s, i) => {
              const isOpen = i === openIdx;
              const Icon = s.icon;
              return (
                <li key={s.code} className="border-b border-ink/10">
                  <button
                    type="button"
                    onClick={() => selectService(i)}
                    aria-expanded={isOpen}
                    aria-controls={`service-panel-${s.code} service-panel-${s.code}-inline`}
                    className={`w-full text-left py-6 md:py-8 pr-6 flex items-center gap-4 md:gap-6 group transition-colors duration-500 ${isOpen ? "text-ink" : "text-ash hover:text-ink"}`}
                    data-cursor="hover"
                    data-testid={`service-rail-${s.code}`}
                  >
                    <span className={`mono-label w-8 shrink-0 ${isOpen ? "text-marine" : "text-ash"}`}>{String(i).padStart(2, "0")}</span>
                    <span className="flex-1 min-w-0">
                      <span className={`mono-label block mb-1 ${isOpen ? "text-marine" : "text-ash"}`}>{s.label}</span>
                      <span className="font-display italic text-2xl md:text-3xl leading-tight tracking-tight block">
                        {s.title}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={`shrink-0 w-10 h-10 border flex items-center justify-center transition-colors duration-500 ${isOpen ? "border-marine text-marine" : "border-ink/25 text-ash group-hover:border-ink/60 group-hover:text-ink"}`}
                      style={{ borderRadius: "999px" }}
                    >
                      {isOpen ? <Minus size={16} strokeWidth={1.4} /> : <Plus size={16} strokeWidth={1.4} />}
                    </span>
                    {isOpen && (
                      <motion.span
                        layoutId="services-rail-marker"
                        aria-hidden
                        className="absolute left-0 h-full w-[3px] bg-marine"
                        style={{ borderRadius: "0 3px 3px 0" }}
                        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                      />
                    )}
                  </button>
                  {/* MOBILE-ONLY inline panel — shows step content right below the
                      tapped service instead of at the bottom of the whole rail. */}
                  {isOpen && (
                    <div className="md:hidden pb-8 border-t border-ink/10" data-testid={`service-panel-mobile-${s.code}`}>
                      {renderActivePanel("mobile")}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          {/* Desktop-only right panel — hidden on mobile since the inline panel above renders instead */}
          <div className="hidden md:block md:col-span-7 md:pl-10">
            {renderActivePanel("desktop")}
          </div>
        </div>
      </LayoutGroup>
    </section>
  );
}
